import { RadioButtonGroup, SingleSelect, TextField } from "@bonliva-crm/ui";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Tooltip
} from "@material-ui/core";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import React, { useEffect } from "react";
import * as reg from "~/common/regexps";
import { ConsultantReferenceType } from "@bonliva-crm/shared";
import { DevTool } from "@hookform/devtools";
import {
  ConsultantReferenceInputNew,
  ConsultantReferenceStatus,
  ReferenceItemFragment
} from "@graphql";
import { useLocale } from "@hooks";

export type InitialValues = {
  name: string;
  position: string;
  areaOfExpertise: LabelValue;
  workPlace: string;
  phone: string;
  email: string;
  formType?: ConsultantReferenceType;
};

type Props = {
  reference?: ReferenceItemFragment | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (
    values: ConsultantReferenceInputNew,
    onSuccess?: (data: ReferenceItemFragment) => void
  ) => void;
  onSendRequest: (data: ReferenceItemFragment) => void;
  areasOfExpertise: string[];
  loading?: boolean;
  cvGranskningApproved?: boolean;
};

export const ReferenceFormModal = ({
  reference,
  open,
  setOpen,
  areasOfExpertise,
  onSubmit,
  onSendRequest,
  loading,
  cvGranskningApproved
}: Props) => {
  const classes = useStyles();
  const { locale } = useLocale();
  const initialValues = getDialogValues(reference);

  const form = useForm<InitialValues>({
    defaultValues: initialValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const [formType, phone, email] = form.watch(["formType", "phone", "email"]);

  useEffect(() => {
    if (open) {
      form.trigger();
    } else {
      form.reset();
    }
  }, [open]);

  // to remove error message from email/phone when one of them is filled
  useEffect(() => {
    form.trigger(["phone", "email"]);
  }, [phone, email]);

  const handleSubmit = (onSuccess?: (data: ReferenceItemFragment) => void) => {
    const _input = {
      ...form.getValues(),
      areaOfExpertise: form.getValues().areaOfExpertise.value
    };
    onSubmit(_input, onSuccess);
  };

  const typeOptions = Object.values(ConsultantReferenceType)
    .filter((type) => type !== ConsultantReferenceType.EvaluationToThreeMonths)
    .map((type) => ({
      label: locale.consultantReferenceType[type],
      value: type
    }));

  //! Check if the form is dirty (hack) react-hook-form does not change isDirty to true
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
  const areaOfExpertiseOptions = areasOfExpertise.toLabelValue();
  const canSubmit = form.formState.isValid && !loading && isDirty;
  const canEditExternalForm = form.formState.isValid && !loading && formType;
  const isEditing = !!initialValues;
  const hasDocument = !!reference?.document;
  const showSendToKvkButton =
    reference?.status !== ConsultantReferenceStatus.RequestSentToKVK &&
    !hasDocument &&
    !reference?.deleted;

  return (
    <FormProvider {...form}>
      <Dialog open={open} maxWidth="sm" fullWidth onClose={() => setOpen(false)}>
        {/* DevTool can affect form state validation */}
        <DevTool control={form.control} placement={"top-right"} />
        <DialogTitle>{isEditing ? locale.editForm : locale.newForm}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    error={!!fieldState.error}
                    fullWidth
                    label={locale.Name}
                    placeholder=""
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="position"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    error={!!fieldState.error}
                    fullWidth
                    label={locale.Position}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} style={{ zIndex: 2 }}>
              <Controller
                name="areaOfExpertise"
                control={form.control}
                render={({ field, fieldState }) => (
                  <div>
                    <SingleSelect
                      {...field}
                      error={!!fieldState.error}
                      inputLabel={locale.Expertise}
                      styleProps={{
                        control: { showBorder: true },
                        menu: { showBorder: true }
                      }}
                      placeholder=""
                      isClearable
                      options={areaOfExpertiseOptions}
                    />
                  </div>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="workPlace"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    error={!!fieldState.error}
                    fullWidth
                    label={locale.ReferenceWorkplace}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={5}>
                <Controller
                  name="phone"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      error={!!fieldState.error}
                      fullWidth
                      label={locale.Phone}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={7}>
                <Controller
                  name="email"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      error={!!fieldState.error}
                      fullWidth
                      label={locale.Email}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="formType"
                control={form.control}
                render={({ field }) => (
                  <RadioButtonGroup
                    {...field}
                    label={"Formulärtyp"}
                    options={typeOptions}
                    disabled={hasDocument}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box className={classes.btnWrap}>
            <Button
              color={"primary"}
              className={classes.cancelBtn}
              onClick={() => setOpen(false)}
            >
              {locale.Cancel}
            </Button>
            <Button
              color="primary"
              variant={"contained"}
              disabled={!canSubmit}
              onClick={() => handleSubmit()}
            >
              {locale.Save}
            </Button>
            {showSendToKvkButton && (
              <Tooltip title={!cvGranskningApproved ? locale.CVIsRequired : ""}>
                <span>
                  <Button
                    color="primary"
                    variant={"contained"}
                    disabled={!canEditExternalForm || !cvGranskningApproved}
                    onClick={() => {
                      handleSubmit((data) => {
                        onSendRequest(data);
                      });
                    }}
                  >
                    {locale.Save} & {locale.Order}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
              color="primary"
              variant={"contained"}
              disabled={!canEditExternalForm}
              onClick={() => {
                handleSubmit((data) => {
                  window.open(data.editLink || data.formLink, "_blank");
                });
              }}
            >
              {locale.Save} & {locale.editForm}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

const getDialogValues = (item: ReferenceItemFragment | null): InitialValues | object => {
  if (!item) {
    return {};
  }
  const { name, position, areaOfExpertise, workPlace, phone, email, formType } = item;

  return {
    name,
    position,
    workPlace,
    phone,
    email,
    formType,
    areaOfExpertise: {
      label: areaOfExpertise,
      value: areaOfExpertise
    }
  };
};

const schema = yup.object().shape(
  {
    name: yup.string().required(),
    position: yup.string().required(),
    workPlace: yup.string().required(),
    areaOfExpertise: yup.object().required(),
    email: yup
      .string()
      .email()
      .nullable(true)
      .when("phone", {
        is: (phone) => !phone,
        then: yup.string().required()
      }),
    phone: yup
      .string()
      .matches(reg.phoneNumber, { excludeEmptyString: true })
      .nullable(true)
      .when("email", {
        is: (email) => !email,
        then: yup.string().required()
      })
  },
  [["phone", "email"]]
);

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    overflow: "hidden"
  },
  btnWrap: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    padding: theme.spacing(2),
    gap: theme.spacing(2)
  },
  cancelBtn: {
    marginRight: "auto"
  }
}));
